import * as React from 'react';
import {
  Button,
  Divider,
  Stack,
  Text,
  UrgentlyCancelModal,
  UrgentlyGoaModal,
  toast,
} from '@relay-towing/ui';
import { ClipboardText, Prohibit, XCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useCancelUrgentlyCall } from '../../hooks/useCancelUrgentlyCall';
import { useReportGoa } from '../../hooks/useReportGoa';
import { useUserCompanies } from '../../hooks/useUserCompanies';
import {
  URGENTLY_INSPECTION_TITLES,
  type UrgentlyInspectionKind,
} from '../../lib/urgentlyInspectionSchema';
import { UrgentlyInspectionSheet } from './UrgentlyInspectionSheet';
import type { CallRecord } from './callRecord';

/** Where the job stands with Urgently, once it is waiting on them or done. */
const STANDING: Record<string, string> = {
  COMPLETED_AWAITING: 'Completed. Waiting for Urgently to approve it.',
  COMPLETED: 'Urgently approved the completion.',
  GOA_AWAITING: 'Reported gone on arrival. Waiting for Urgently to confirm.',
  GOA: 'Urgently confirmed it gone on arrival.',
  CANCELED: 'Cancelled with Urgently.',
};

/**
 * Not the company's job yet (offered, or accepted and waiting for Urgently
 * to assign it), or finished: nothing to inspect, report or cancel.
 */
const NOT_ACTIVE = new Set([
  'OFFERED',
  'ACCEPTED_AWAITING',
  'COMPLETED',
  'COMPLETED_AWAITING',
  'GOA',
  'GOA_AWAITING',
  'CANCELED',
  'FAILED',
  'DECLINED',
]);

const failure = (error: unknown, what: string) => {
  const message = (error as Error | null)?.message;
  return message ? `${what}: ${message}` : `${what}.`;
};

/**
 * What an Urgently job asks of the driver beyond the call, as the old
 * screen's Urgently panel had it: the two inspections of the vehicle (on
 * site and at drop off), reporting it gone on arrival, and cancelling it,
 * each sent on to Urgently by the server. Completing it is the status
 * control's, which asks Urgently's questions. Under these, where the job
 * stands with Urgently.
 *
 * The server lets a driver on the call cancel it or report it GOA, and
 * `calls.cancel` lets the office do so on any call they see; an inspection
 * is a change to the call, so it follows `editable`. This only hides what
 * the role may not do.
 */
export function CallUrgently({
  call,
  editable,
  cancels,
}: {
  call: CallRecord;
  /** May change the call: a driver on it, or the office with calls.edit. */
  editable: boolean;
  /** May cancel it or report it GOA: a driver on it, or calls.cancel. */
  cancels: boolean;
}) {
  const navigate = useNavigate();
  // The selected company, not the driver row's: the office may cancel too.
  const { selectedCompanyId } = useUserCompanies();
  const reportGoa = useReportGoa();
  const cancelJob = useCancelUrgentlyCall();
  const [inspecting, setInspecting] =
    React.useState<UrgentlyInspectionKind | null>(null);
  const [reporting, setReporting] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  const active = !NOT_ACTIVE.has(call.status);
  const standing = STANDING[call.status];
  if (!standing && !(active && (editable || cancels))) return null;

  const goa = async (goaReason: string, notes?: string) => {
    try {
      await reportGoa.mutateAsync({ dispatchId: call.id, goaReason, notes });
    } catch (error) {
      toast.error(failure(error, 'The GOA could not be reported'));
      return;
    }
    setReporting(false);
    toast.success('Reported gone on arrival.');
    navigate('/calls', { replace: true });
  };

  const cancel = async (cancelReason: string, notes?: string) => {
    if (!selectedCompanyId) return;
    try {
      await cancelJob.mutateAsync({
        dispatchId: call.id,
        companyId: selectedCompanyId,
        cancelReason,
        notes,
      });
    } catch (error) {
      toast.error(failure(error, 'The job could not be cancelled'));
      return;
    }
    setCancelling(false);
    toast.success('Job cancelled.');
    navigate('/calls', { replace: true });
  };

  // Its own rule above it, as each of the call's sections has, so a job
  // with nothing to show draws no empty section.
  return (
    <>
      <Divider />
      <Stack gap={4}>
        <Text as="h2" variant="h2">
          Urgently
        </Text>
        {standing ? <Text tone="muted">{standing}</Text> : null}
        {active && editable ? (
          <>
            {(['ON_SITE', 'ON_DROP_OFF'] as const).map((kind) => (
              <Button
                key={kind}
                variant="outline"
                leadingIcon={<ClipboardText />}
                onClick={() => setInspecting(kind)}
              >
                {URGENTLY_INSPECTION_TITLES[kind]}
              </Button>
            ))}
            <UrgentlyInspectionSheet
              callId={call.id}
              kind={inspecting ?? 'ON_SITE'}
              open={inspecting !== null}
              onOpenChange={(open) => {
                if (!open) setInspecting(null);
              }}
            />
          </>
        ) : null}
        {active && cancels ? (
          <>
            <Button
              variant="outline"
              leadingIcon={<Prohibit />}
              onClick={() => setReporting(true)}
            >
              Report GOA
            </Button>
            <Button
              variant="outline"
              leadingIcon={<XCircle />}
              onClick={() => setCancelling(true)}
            >
              Cancel job
            </Button>
            <UrgentlyGoaModal
              presentation="sheet"
              isOpen={reporting}
              onSubmit={(reason, notes) => void goa(reason, notes)}
              onCancel={() => setReporting(false)}
              isSubmitting={reportGoa.isPending}
            />
            <UrgentlyCancelModal
              presentation="sheet"
              isOpen={cancelling}
              onSubmit={(reason, notes) => void cancel(reason, notes)}
              onCancel={() => setCancelling(false)}
              isSubmitting={cancelJob.isPending}
            />
          </>
        ) : null}
      </Stack>
    </>
  );
}
